import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { connect } from "react-redux"
import axios from "axios"
import AddCustomMaterialPopup from "./AddCustomMaterialPopup.tsx"
import SendQuotePopup from "./SendQuotePopup.tsx"
import MaterialsListPreview from "./MaterialsListPreview.tsx"
import styles from "./CalculadoraDeMateriales.module.scss"
import {
  extractProjectAutofillDraftIdFromResponse,
  type DocumentationPipelineStageProgress,
} from "../../../services/projectAutofillDraftService.ts"
import {
  extractMaterialsAutofillDraftPayload,
  getMaterialsQuotationAutofillDraft,
  materialAutofillLinesToSelectedMaterials,
  postMaterialsQuotationAutofillDraftFromDocument,
  waitForMaterialsQuotationDocumentationPipeline,
  type MaterialsAutofillAnalysisContext,
} from "../../../services/materialsQuotationAutofillDraftService.ts"
import { getCategoriesWithMaterials } from "../../../store/actions/categoryActions"
import {
  closeMainModal,
  editSubquotationMaterials,
  getMaterialsBySubquotationId,
  openMainModal,
} from "../../../store/actions/profileActions"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { createPortal } from "react-dom"
import CalculadoraDeMaterialesSkeleton from "./CalculadoraDeMaterialesSkeleton.jsx"
import { fireAnalytics } from "../../../utils/firebase"
import { useActionGuard } from "../../../hooks/useActionGuard.ts"
import { useMaterialDetailModal } from "../../../contexts/MaterialDetailModalContext.tsx"
import {
  MATERIALS_FOR_ESTIMATION_STATE_KEY,
  MATERIALS_FOR_QUOTATION_STATE_KEY,
  type CalculadoraEstimationTransferLocationState,
  type CalculadoraQuotationTransferLocationState,
  type CalculadoraProps,
  type CalculadoraReduxState,
  type CatalogMaterial,
  type CategoriesResponse,
  type CategoryFromApi,
  isMaterialOutOfCatalog,
  type SelectedMaterial,
} from "../../../types/calculadoraDeMateriales.ts"
import { FREQUENT_MATERIAL_NAMES, SEARCH_RESULTS_LIMIT } from "../../../constants/calculadoraDeMateriales.ts"
import CalculadoraPageHeader from "./components/CalculadoraPageHeader.tsx"
import CalculadoraIntegratedWelcome from "./components/CalculadoraIntegratedWelcome.tsx"
import MaterialsQuotationPipelineLoader from "./components/MaterialsQuotationPipelineLoader.tsx"
import CalculadoraCatalogToolbar from "./components/CalculadoraCatalogToolbar.tsx"
import CalculadoraExpandedMaterialsList from "./components/CalculadoraExpandedMaterialsList.tsx"
import CalculadoraMaterialsEmptyPanel from "./components/CalculadoraMaterialsEmptyPanel.tsx"
import CalculadoraSummaryColumn from "./components/CalculadoraSummaryColumn.tsx"
import CalculadoraMaterialAddedToast from "./components/CalculadoraMaterialAddedToast.tsx"

function enrichMaterialsWithCatalogPrices(
  materials: SelectedMaterial[],
  categoryData: CategoryFromApi[] | undefined
): SelectedMaterial[] {
  if (!categoryData?.length) return materials
  const byId = new Map<string, CatalogMaterial>()
  for (const cat of categoryData) {
    for (const m of cat.materials ?? []) {
      if (m?.id == null) continue
      byId.set(String(m.id), m)
    }
  }
  return materials.map((row) => {
    if (row.price != null && Number.isFinite(Number(row.price))) return row
    const cm = byId.get(String(row.id))
    const p = cm?.price
    if (p != null && Number.isFinite(Number(p))) {
      return { ...row, price: Number(p) }
    }
    return row
  })
}

const CalculadoraDeMateriales = ({
  getCategoriesWithMaterials,
  categoriesWithMaterials,
  quotationData,
  materialsBySubquotation,
  openMainModal,
  userData,
  getMaterialsBySubquotationId,
  closeMainModal,
  editSubquotationMaterials,
}: CalculadoraProps) => {
  const { id } = useParams<{ id?: string }>()
  const location = useLocation()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { guard } = useActionGuard()
  const isEstimacionDeCostos = location.pathname.includes("estimacion-costos")

  const [categories, setCategories] = useState<CategoriesResponse>(categoriesWithMaterials)
  const [selectedMaterials, setSelectedMaterials] = useState<SelectedMaterial[]>([])
  const [expandedCategories, setExpandedCategories] = useState<string[]>([])
  const [selectedCategory, setSelectedCategory] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredMaterials, setFilteredMaterials] = useState<
    (CatalogMaterial & { category: string; categoryId: string })[]
  >([])
  const [showAddCustomPopup, setShowAddCustomPopup] = useState(false)
  const [showSendQuotePopup, setShowSendQuotePopup] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [isSyncingMaterials, setIsSyncingMaterials] = useState(true)
  const [originalMaterials, setOriginalMaterials] = useState<SelectedMaterial[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(!!id)
  const [searchInputActive, setSearchInputActive] = useState(false)
  const [showMaterialAddedToast, setShowMaterialAddedToast] = useState(false)
  const prevMaterialsCountRef = useRef(0)
  const [searchRequested, setSearchRequested] = useState(false)
  const [searchResultsExpanded, setSearchResultsExpanded] = useState(false)
  const [searchHighlightIndex, setSearchHighlightIndex] = useState(-1)
  const [lastAddedMaterialId, setLastAddedMaterialId] = useState<number | string | null>(null)
  const [materialsAutofillDraftId, setMaterialsAutofillDraftId] = useState<string | null>(null)
  const [materialsAutofillAnalysisContext, setMaterialsAutofillAnalysisContext] =
    useState<MaterialsAutofillAnalysisContext | null>(null)
  const [materialsWelcomeDismissed, setMaterialsWelcomeDismissed] = useState(false)
  const [estimationWelcomeDismissed, setEstimationWelcomeDismissed] = useState(false)
  const [isMaterialsFileAnalyzing, setIsMaterialsFileAnalyzing] = useState(false)
  const [materialsFilePipeline, setMaterialsFilePipeline] = useState<string | null>(null)
  const [materialsFileStageProgress, setMaterialsFileStageProgress] =
    useState<DocumentationPipelineStageProgress | null>(null)
  const [materialsFileLabel, setMaterialsFileLabel] = useState<string | null>(null)
  const [materialsFileSize, setMaterialsFileSize] = useState<number | null>(null)
  const { openMaterialDetail } = useMaterialDetailModal()
  const estimationTransferAppliedRef = useRef(false)
  const quotationFromEstimationAppliedRef = useRef(false)
  const routeQuotationIdRef = useRef<string | undefined>(undefined)

  const isCreateQuotationFlow = !isEstimacionDeCostos && !isEditMode
  const showIntegratedMaterialsWelcome =
    isCreateQuotationFlow && selectedMaterials.length === 0 && !materialsWelcomeDismissed
  const showIntegratedEstimationWelcome =
    isEstimacionDeCostos && !isEditMode && selectedMaterials.length === 0 && !estimationWelcomeDismissed
  const showManualSecondEmptyState =
    isCreateQuotationFlow && selectedMaterials.length === 0 && materialsWelcomeDismissed
  const showEstimationSecondEmptyState =
    isEstimacionDeCostos && !isEditMode && selectedMaterials.length === 0 && estimationWelcomeDismissed
  const showReplaceAutofillFileAction =
    (isCreateQuotationFlow || (isEstimacionDeCostos && !isEditMode)) &&
    materialsAutofillDraftId != null &&
    selectedMaterials.length > 0 &&
    !isMaterialsFileAnalyzing
  const hideCreateTopHeader =
    (isCreateQuotationFlow && materialsWelcomeDismissed) ||
    (isEstimacionDeCostos && !isEditMode)

  const categoryOptions = useMemo(() => {
    const sortedCategories =
      categories?.data
        ?.slice()
        .sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""))
        ?.map((category) => ({
          value: String(category.id),
          label: `${category.name} (${category.materials?.length ?? 0})`,
        })) ?? []

    return [
      {
        value: "custom",
        label: "Crear material personalizado",
        description: "Configura materiales a medida para tu proyecto",
        variant: "accent" as const,
      },
      ...sortedCategories,
    ]
  }, [categories])

  const navigate = useNavigate()

  const handleTransferToEstimation = useCallback(() => {
    navigate("/cotizaciondemateriales/estimacion-costos", {
      state: { [MATERIALS_FOR_ESTIMATION_STATE_KEY]: selectedMaterials },
    })
  }, [navigate, selectedMaterials])

  const handleRequestQuotesFromEstimation = useCallback(() => {
    navigate("/cotizaciondemateriales/crear", {
      state: { [MATERIALS_FOR_QUOTATION_STATE_KEY]: selectedMaterials },
    })
  }, [navigate, selectedMaterials])

  useEffect(() => {
    if (!isEstimacionDeCostos) {
      estimationTransferAppliedRef.current = false
    }
  }, [isEstimacionDeCostos])

  useEffect(() => {
    if (isEstimacionDeCostos || isEditMode) {
      quotationFromEstimationAppliedRef.current = false
    }
  }, [isEditMode, isEstimacionDeCostos])

  useEffect(() => {
    if (isEstimacionDeCostos || isEditMode) return
    const st = location.state as CalculadoraQuotationTransferLocationState | null
    const snapshot = st?.[MATERIALS_FOR_QUOTATION_STATE_KEY]
    if (!Array.isArray(snapshot) || snapshot.length === 0) return
    if (quotationFromEstimationAppliedRef.current) return
    setSelectedMaterials(snapshot as SelectedMaterial[])
    setMaterialsWelcomeDismissed(true)
    setSearchRequested(true)
    setMaterialsAutofillDraftId(null)
    setMaterialsAutofillAnalysisContext(null)
    quotationFromEstimationAppliedRef.current = true
    navigate("/cotizaciondemateriales/crear", { replace: true, state: {} })
  }, [isEditMode, isEstimacionDeCostos, location.state, navigate])

  useEffect(() => {
    if (!isEstimacionDeCostos) return
    const st = location.state as CalculadoraEstimationTransferLocationState | null
    const snapshot = st?.[MATERIALS_FOR_ESTIMATION_STATE_KEY]
    if (!Array.isArray(snapshot) || snapshot.length === 0) return
    if (estimationTransferAppliedRef.current) return
    if (!categories.data?.length) return
    const enriched = enrichMaterialsWithCatalogPrices(snapshot, categories.data)
    setSelectedMaterials(enriched)
    setEstimationWelcomeDismissed(true)
    setSearchRequested(true)
    estimationTransferAppliedRef.current = true
    navigate("/cotizaciondemateriales/estimacion-costos", { replace: true, state: {} })
  }, [isEstimacionDeCostos, location.state, categories.data, navigate])

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      await getCategoriesWithMaterials()
      setIsLoading(false)
    }
    fetchData()
    if (userData?.id) {
      fireAnalytics("create_quotation", { action: "create_quotation", user_info: userData.id })
    }
  }, [])

  useEffect(() => {
    setCategories(categoriesWithMaterials)
  }, [categoriesWithMaterials])

  useEffect(() => {
    setIsEditMode(!!id)
    const next = id
    const prev = routeQuotationIdRef.current
    if (prev !== next) {
      routeQuotationIdRef.current = next
      setSelectedMaterials([])
    }
  }, [id])

  useEffect(() => {
    if (isEditMode && quotationData && isSyncingMaterials) {
      const fetchMaterials = async () => {
        try {
          setIsLoading(true)
          const subId = quotationData?.subquotation?.[0]?.id
          if (subId != null) {
            await getMaterialsBySubquotationId(subId)
          }

          if (!materialsBySubquotation) {
            return
          }

          const subCotizaciones = Object.values(materialsBySubquotation)
          const formattedMaterials: SelectedMaterial[] = subCotizaciones.flatMap((subCotizacion) =>
            subCotizacion.flatMap((category) =>
              (category.materials || []).map((material) => ({
                id: material.id,
                name: material.name,
                unit: material.unit ?? "N/A",
                quantity: material.amount ?? 1,
                category: category.name ?? "Sin Categoría",
              }))
            )
          )

          setSelectedMaterials(formattedMaterials)
        } catch (error) {
          console.error("Error al obtener materiales:", error)
        } finally {
          setIsSyncingMaterials(false)
          setIsLoading(false)
        }
      }

      fetchMaterials()
    }
  }, [isEditMode, quotationData, isSyncingMaterials])

  useEffect(() => {
    if (isEditMode && materialsBySubquotation) {
      const subCotizaciones = Object.values(materialsBySubquotation)
      const formattedMaterials: SelectedMaterial[] = subCotizaciones.flatMap((subCotizacion) =>
        subCotizacion.flatMap((category) =>
          (category.materials || []).map((material) => ({
            id: material.id,
            name: material.name,
            unit: material.unit ?? "N/A",
            quantity: material.amount ?? 1,
            category: category.name ?? "Sin Categoría",
          }))
        )
      )
      setOriginalMaterials(formattedMaterials)
    }
  }, [materialsBySubquotation, isEditMode])

  useEffect(() => {
    const count = selectedMaterials.length
    const prev = prevMaterialsCountRef.current
    if (prev + 1 === count && count > 0) {
      setShowMaterialAddedToast(true)
    }
    prevMaterialsCountRef.current = count
  }, [selectedMaterials.length])

  useEffect(() => {
    if (!showMaterialAddedToast) return
    const timer = setTimeout(() => setShowMaterialAddedToast(false), 3000)
    return () => clearTimeout(timer)
  }, [showMaterialAddedToast])

  useEffect(() => {
    if (lastAddedMaterialId == null) return
    const scrollToAdded = () => {
      const el = document.querySelector(`[data-summary-material-id="${lastAddedMaterialId}"]`)
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" })
      }
    }
    const raf = requestAnimationFrame(() => {
      scrollToAdded()
    })
    const clearHighlight = setTimeout(() => setLastAddedMaterialId(null), 2200)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(clearHighlight)
    }
  }, [lastAddedMaterialId])

  const hasChanges = (): boolean => {
    if (selectedMaterials.length !== originalMaterials.length) {
      return true
    }

    return selectedMaterials.some((material, index) => {
      const originalMaterial = originalMaterials[index]
      if (!originalMaterial) return true
      return material.id !== originalMaterial.id || Number(material.quantity) !== Number(originalMaterial.quantity)
    })
  }

  const removeMaterial = (materialId: number | string) => {
    setSelectedMaterials((prev) => prev.filter((m) => m.id !== materialId))
  }

  useEffect(() => {
    if (selectedMaterials.length === 0 && isCreateQuotationFlow) {
      setMaterialsAutofillDraftId(null)
      setMaterialsAutofillAnalysisContext(null)
      setMaterialsWelcomeDismissed(false)
    }
  }, [selectedMaterials.length, isCreateQuotationFlow])

  useEffect(() => {
    if (selectedMaterials.length === 0 && isEstimacionDeCostos) {
      setEstimationWelcomeDismissed(false)
    }
  }, [selectedMaterials.length, isEstimacionDeCostos])

  useEffect(() => {
    if (showIntegratedMaterialsWelcome || showIntegratedEstimationWelcome) {
      setSelectedCategory("")
      setExpandedCategories([])
      setSearchRequested(false)
    }
  }, [showIntegratedMaterialsWelcome, showIntegratedEstimationWelcome])

  useEffect(() => {
    if (!searchTerm) {
      setFilteredMaterials([])
      return
    }

    const removeAccents = (str: string) => {
      return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    }

    const normalizedSearchTerm = removeAccents(searchTerm.toLowerCase())

    const filtered = (categories.data ?? []).flatMap((category) => {
      return (category.materials ?? [])
        .filter((material) => {
          const normalizedMaterialName = removeAccents(material.name.toLowerCase())
          return normalizedMaterialName.includes(normalizedSearchTerm)
        })
        .map((material) => ({
          ...material,
          category: category.name,
          categoryId: String(category.id),
        }))
    })

    setFilteredMaterials(filtered)
  }, [searchTerm, categories])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setSearchResultsExpanded(false)
    setSearchHighlightIndex(-1)
  }

  const visibleMaterials = useMemo(() => {
    if (!searchResultsExpanded && filteredMaterials.length > SEARCH_RESULTS_LIMIT) {
      return filteredMaterials.slice(0, SEARCH_RESULTS_LIMIT)
    }
    return filteredMaterials
  }, [filteredMaterials, searchResultsExpanded])

  const hasMoreResults = filteredMaterials.length > SEARCH_RESULTS_LIMIT
  const totalItems =
    filteredMaterials.length > 0
      ? visibleMaterials.length + (hasMoreResults && !searchResultsExpanded ? 1 : 0)
      : 1

  const toggleMaterial = useCallback(
    (material: CatalogMaterial & { category?: string }, categoryId: string) => {
      const matchedCategory = categories?.data?.find((cat) => String(cat.id) === String(categoryId))
      const category = matchedCategory?.name ?? categoryId

      setSelectedMaterials((prev) => {
        const existingIndex = prev.findIndex((m) => m.id === material.id)
        if (existingIndex >= 0) {
          return prev.filter((m) => m.id !== material.id)
        }
        setLastAddedMaterialId(material.id)
        return [
          ...prev,
          {
            ...material,
            quantity: 1,
            category,
            unit: material.unit ?? "N/A",
            categoryId: matchedCategory?.id,
          } as SelectedMaterial,
        ]
      })
      setSearchTerm("")
    },
    [categories]
  )

  const selectCategoryAndToggleMaterial = useCallback(
    (
      material: CatalogMaterial & { categoryId: string; categoryName?: string },
      categoryId: string
    ) => {
      const cid = String(categoryId)
      if (cid) {
        setSelectedCategory(cid)
        setExpandedCategories([cid])
      }
      toggleMaterial(material, cid)
    },
    [toggleMaterial]
  )

  const selectMaterialFromSearch = useCallback(
    (material: CatalogMaterial & { category?: string; categoryId: string }) => {
      const cid = String(material.categoryId)
      if (cid) {
        setSelectedCategory(cid)
        setExpandedCategories([cid])
      }
      toggleMaterial(material, cid)
    },
    [toggleMaterial]
  )

  useEffect(() => {
    if (!searchTerm) {
      setSearchHighlightIndex(-1)
      return
    }
    setSearchHighlightIndex(0)
  }, [searchTerm, filteredMaterials.length])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!searchTerm) return
      const isNoResults = filteredMaterials.length === 0
      const itemCount = isNoResults ? 1 : totalItems

      if (e.key === "ArrowDown") {
        e.preventDefault()
        setSearchHighlightIndex((prev) => (prev < itemCount - 1 ? prev + 1 : prev))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setSearchHighlightIndex((prev) => (prev > 0 ? prev - 1 : -1))
      } else if (e.key === "Enter" && searchHighlightIndex >= 0) {
        e.preventDefault()
        if (isNoResults) {
          setShowAddCustomPopup(true)
          setSearchTerm("")
        } else if (searchHighlightIndex < visibleMaterials.length) {
          const material = visibleMaterials[searchHighlightIndex]
          selectMaterialFromSearch(material)
          setSearchTerm("")
        } else if (hasMoreResults && !searchResultsExpanded) {
          setSearchResultsExpanded(true)
          setSearchHighlightIndex(0)
        }
      } else if (e.key === "Escape") {
        setSearchTerm("")
        searchInputRef.current?.blur()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [
    searchTerm,
    searchHighlightIndex,
    totalItems,
    visibleMaterials,
    filteredMaterials.length,
    hasMoreResults,
    searchResultsExpanded,
    selectMaterialFromSearch,
  ])

  const toggleCategory = (categoryId: string) => {
    if (categoryId === "custom") {
      setShowAddCustomPopup(true)
      return
    }

    if (!categoryId) {
      setSelectedCategory("")
      setExpandedCategories([])
      return
    }

    if (categoryId === selectedCategory) {
      setSelectedCategory("")
      setExpandedCategories([])
      return
    }

    setSelectedCategory(categoryId)
    setExpandedCategories((prevCategories) => (prevCategories.includes(categoryId) ? [] : [categoryId]))
  }

  const updateQuantity = (materialId: number | string, quantity: number) => {
    setSelectedMaterials((prev) => prev.map((m) => (m.id === materialId ? { ...m, quantity } : m)))
  }

  const addCustomMaterial = (newMaterial: SelectedMaterial & { categoryId?: string; category?: string }) => {
    if (!Array.isArray(categories.data)) {
      return
    }

    const categoryId = newMaterial.categoryId ?? categories.data.find((c) => c.name === newMaterial.category)?.id

    if (categoryId != null && categories.data.findIndex((c) => String(c.id) === String(categoryId)) !== -1) {
      setCategories((prevCategories) => {
        const newCategories = { ...prevCategories }
        newCategories.data = [...(prevCategories.data ?? [])]
        return newCategories
      })
    } else {
      setCategories((prevCategories) => ({
        ...prevCategories,
        data: [
          ...(prevCategories.data ?? []),
          {
            id: newMaterial.categoryId ?? "custom",
            name: newMaterial.category ?? "Personalizado",
            materials: [{ ...newMaterial, temporary: true } as CatalogMaterial],
            jurisdiction: (newMaterial as { jurisdiction?: unknown }).jurisdiction,
            currency: newMaterial.currency,
          },
        ],
      }))
    }

    const addedMaterial = {
      ...newMaterial,
      category:
        categories?.data?.find((c) => c.id === (newMaterial.categoryId ?? newMaterial.category))?.name ??
        newMaterial.category ??
        "Sin Categoría",
      unit: newMaterial.unit,
      quantity: newMaterial.quantity ?? 1,
      temporary: true,
      currency: newMaterial.currency,
    } as SelectedMaterial
    setSelectedMaterials((prevSelected) => [...prevSelected, addedMaterial])
    setLastAddedMaterialId(addedMaterial.id)

    setShowAddCustomPopup(false)
  }

  const total = selectedMaterials?.reduce((sum, m) => sum + (m.price ?? 0) * m.quantity, 0) ?? 0

  const projectName = quotationData?.quotation?.name
  const headerTitle = isEditMode
    ? "Editar cotización"
    : isEstimacionDeCostos
      ? "Crear estimación de costos"
      : "Publicá tu lista y recibí presupuestos"
  const headerSubtitle = isEstimacionDeCostos
    ? projectName
      ? `Mantén el presupuesto actualizado para ${projectName}. Más de 3000 materiales disponibles.`
      : "Controla los costos estimados de tu proyecto. Más de 3000 materiales disponibles."
    : isEditMode
      ? projectName
        ? `Actualizá los materiales para ${projectName}`
        : "Actualizá la lista de materiales antes de enviar"
      : "Armá el listado y recibí ofertas de proveedores de tu zona"
  const groupedMaterials = selectedMaterials.reduce<Record<string, SelectedMaterial[]>>((acc, material) => {
    if (!acc[material.category]) {
      acc[material.category] = []
    }
    acc[material.category].push(material)
    return acc
  }, {})

  const formattedTotal = new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
  }).format(total)

  const handleSendQuoteClick = guard(
    "send_quotation",
    async () => {
      if (isEditMode) {
        const confirmation = await new Promise<boolean>((resolve) => {
          openMainModal({
            title: "¿Estás seguro de que deseas guardar los cambios en la cotización?",
            body: "Se le notificará a los proveedores que se han realizado cambios en la cotización.",
            iconType: "warning",
            noLabel: "Cancelar",
            yesLabel: "Guardar cambios",
            confirmHandler: () => {
              closeMainModal()
              setIsLoading(true)
              resolve(true)
            },
            customCloseMainModal: () => {
              closeMainModal()
              resolve(false)
            },
          })
        })

        if (confirmation && quotationData?.subquotation?.[0]?.id != null) {
          const formattedMaterials = selectedMaterials.map((material) => {
            const category = categories?.data?.find((cat) => cat.name === material.category)
            return {
              category_id: material.categoryId ?? category?.id ?? 0,
              material_id: material.id,
              temporary: material.temporary ?? false,
              amount: material.quantity,
              name: material.name,
              unit: material.unit,
            }
          })

          try {
            await editSubquotationMaterials(
              quotationData.subquotation[0].id,
              formattedMaterials.map((m) => ({
                ...m,
                category_id: Number(m.category_id) || 0,
              })),
              navigate
            )
          } catch (error) {
            console.error("Error al editar los materiales:", error)
          } finally {
            setIsLoading(false)
          }
        }
      } else {
        setShowSendQuotePopup(true)
      }
    },
    () => {
      if (isEditMode && quotationData?.quotation?.user?.id) {
        fireAnalytics("edit_quotation", { edify_user_id: quotationData.quotation!.user!.id })
      } else if (quotationData?.quotation?.user?.id) {
        fireAnalytics("send_quotation_step1", { edify_user_id: quotationData.quotation!.user!.id })
      }
    }
  )

  const handleQuoteSuccess = () => {
    setSelectedMaterials([])
    setMaterialsAutofillDraftId(null)
    setMaterialsAutofillAnalysisContext(null)
  }

  const runMaterialsFile = useCallback(
    async (file: File) => {
      setMaterialsAutofillAnalysisContext(null)
      setIsMaterialsFileAnalyzing(true)
      setMaterialsFilePipeline(null)
      setMaterialsFileStageProgress(null)
      setMaterialsFileLabel(file.name?.trim() || null)
      setMaterialsFileSize(typeof file.size === "number" ? file.size : null)
      try {
        const fromDoc = await postMaterialsQuotationAutofillDraftFromDocument(file)
        const draftId =
          fromDoc.mode === "async"
            ? fromDoc.draftId
            : extractProjectAutofillDraftIdFromResponse(fromDoc.body)
        if (!draftId) {
          openMainModal({
            title: "No se pudo crear el borrador",
            body: "No se recibió un borrador del servidor. Probá de nuevo o arrancá paso a paso desde el catálogo.",
            isSuccess: true,
            iconType: "error",
            confirmHandler: null,
          })
          return
        }
        await waitForMaterialsQuotationDocumentationPipeline(
          draftId,
          ({ loaderPhase, status }) => {
            setMaterialsFilePipeline(loaderPhase)
            setMaterialsFileStageProgress(status.stageProgress ?? null)
            setMaterialsFileLabel(status.primaryFileOriginalName ?? file.name?.trim() ?? null)
            setMaterialsFileSize(status.primaryFileSizeBytes ?? file.size ?? null)
          }
        )
        const rawDraft = await getMaterialsQuotationAutofillDraft(draftId)
        const { materialLines, analysisContext } = extractMaterialsAutofillDraftPayload(rawDraft)
        const catRows = categories.data ?? []
        const mapped = materialAutofillLinesToSelectedMaterials(materialLines ?? [], catRows)
        setMaterialsAutofillDraftId(draftId)
        setMaterialsAutofillAnalysisContext(analysisContext ?? null)
        setMaterialsWelcomeDismissed(true)
        setSelectedMaterials(mapped)
        setSearchRequested(true)
      } catch (e: unknown) {
        const named = e as { name?: string; userMessage?: string; message?: string }
        if (named.name === "AbortError") {
          return
        }
        let msg =
          "No se pudo procesar el archivo. Probá con otro formato o arrancá paso a paso desde el catálogo."
        if (axios.isAxiosError(e)) {
          const data = e.response?.data as Record<string, unknown> | undefined
          const m = data?.message ?? data?.error ?? data?.mensaje
          if (typeof m === "string" && m.trim()) msg = m.trim()
        } else if (typeof named.userMessage === "string" && named.userMessage.trim()) {
          msg = named.userMessage.trim()
        } else if (typeof named.message === "string" && named.message.trim()) {
          msg = named.message.trim()
        }
        openMainModal({
          title: "No se pudo analizar el documento",
          body: msg,
          isSuccess: true,
          iconType: "error",
          confirmHandler: null,
        })
        setMaterialsAutofillAnalysisContext(null)
      } finally {
        setIsMaterialsFileAnalyzing(false)
        setMaterialsFilePipeline(null)
        setMaterialsFileStageProgress(null)
        setMaterialsFileLabel(null)
        setMaterialsFileSize(null)
      }
    },
    [categories.data, openMainModal]
  )

  const clearSearch = useCallback(() => {
    setSearchTerm("")
    setFilteredMaterials([])
    setSearchResultsExpanded(false)
    setSearchHighlightIndex(-1)
  }, [])

  const handleRequestReplaceAutofillFile = useCallback(() => {
    openMainModal({
      title: "¿Subir otro archivo?",
      body: "La lista que generamos a partir de tu documento se va a descartar. Si solo querés sumar o corregir materiales, podés hacerlo desde esta misma pantalla. Usá esta opción cuando querés analizar otro archivo desde cero.",
      iconType: "warning",
      yesLabel: "Sí, subir otro archivo",
      noLabel: "Cancelar",
      confirmHandler: () => {
        closeMainModal()
        setSelectedMaterials([])
        setMaterialsAutofillDraftId(null)
        setMaterialsAutofillAnalysisContext(null)
        setSearchRequested(false)
        setSelectedCategory("")
        setExpandedCategories([])
        clearSearch()
      },
      customCloseMainModal: () => {
        closeMainModal()
      },
    })
  }, [clearSearch, closeMainModal, openMainModal])

  const handleSearchLinkClick = () => {
    setSearchRequested(true)
    setSearchInputActive(true)
    setTimeout(() => {
      searchInputRef.current?.focus()
      searchInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 100)
  }

  const frequentMaterials = useMemo(() => {
    const normalize = (value: string) =>
      value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
    const byId = new Map<string, CatalogMaterial & { categoryId: string; categoryName: string }>()
    for (const category of categories.data ?? []) {
      for (const material of category.materials ?? []) {
        const key = String(material.id)
        if (byId.has(key)) continue
        byId.set(key, {
          ...material,
          categoryId: String(category.id),
          categoryName: category.name,
        })
      }
    }
    const all = Array.from(byId.values())
    const selected = new Set<string>()
    const out: Array<CatalogMaterial & { categoryId: string; categoryName: string }> = []
    const frequentNamesWithoutLastTwo = FREQUENT_MATERIAL_NAMES.slice(0, -2)
    for (const frequentName of frequentNamesWithoutLastTwo) {
      const match = all.find((material) => {
        const name = normalize(material.name)
        return name.includes(frequentName) && !selected.has(String(material.id))
      })
      if (!match) continue
      selected.add(String(match.id))
      out.push(match)
      if (out.length >= 8) break
    }
    if (out.length >= 6) return out
    for (const material of all) {
      if (selected.has(String(material.id))) continue
      selected.add(String(material.id))
      out.push(material)
      if (out.length >= 8) break
    }
    return out
  }, [categories.data])

  const totalCatalogMaterials = useMemo(() => {
    const ids = new Set<string>()
    for (const category of categories.data ?? []) {
      for (const material of category.materials ?? []) {
        if (material?.id == null) continue
        ids.add(String(material.id))
      }
    }
    return ids.size
  }, [categories.data])

  const totalCatalogMaterialsLabel = new Intl.NumberFormat("es-AR").format(totalCatalogMaterials)

  const hasExpandedCategoryView =
    Boolean(selectedCategory) &&
    Boolean(expandedCategories?.includes(selectedCategory))

  const compactMaterialsCenter =
    (Boolean(selectedCategory) || selectedMaterials.length > 0 || searchRequested) &&
    !hasExpandedCategoryView

  let titleWhenEmpty: string
  let subTitleWhenEmpty: string
  if (compactMaterialsCenter) {
    if (isEstimacionDeCostos) {
      if (selectedMaterials.length > 0) {
        titleWhenEmpty = "Revisá tu lista y sumá lo que falte"
        subTitleWhenEmpty =
          "Mirá cantidades y precios en el resumen. Para nuevas líneas usá la barra superior; acá podés sumar rápido con materiales frecuentes o crear uno personalizado."
      } else if (searchRequested) {
        titleWhenEmpty = "Elegí materiales desde la búsqueda"
        subTitleWhenEmpty =
          "Tocá un material en los resultados de la lista desplegable superior para sumarlo a la lista."
      } else {
        titleWhenEmpty = "Elegí materiales de la categoría"
        subTitleWhenEmpty =
          "Usá el selector de categorías arriba y elegí ítems del catálogo; podés volver acá para atajos de frecuentes."
      }
    } else if (selectedMaterials.length > 0) {
      titleWhenEmpty = "Revisá el listado y sumá lo que falte"
      subTitleWhenEmpty =
        "Chequeá cantidades y unidades en el resumen a la derecha. Para sumar más usá categoría o búsqueda en la barra superior; acá tenés materiales frecuentes y la opción de material personalizado."
    } else if (searchRequested) {
      titleWhenEmpty = "Elegí materiales desde la búsqueda"
      subTitleWhenEmpty =
        "Tocá un material en los resultados de la lista desplegable superior para agregarlo a tu lista."
    } else {
      titleWhenEmpty = "Elegí materiales de la categoría"
      subTitleWhenEmpty =
        "Desplegá una categoría en el selector superior y elegí del catálogo; cuando quieras, seguí usando esta zona para accesos rápidos."
    }
  } else if (isEstimacionDeCostos) {
    titleWhenEmpty =
      selectedMaterials.length > 0
        ? "Para continuar seleccioná una categoría"
        : "Comenzá tu estimación"
    subTitleWhenEmpty =
      selectedMaterials.length > 0
        ? "Seleccioná una categoría o buscá materiales específicos para continuar con tu estimación de costos."
        : "Agregá materiales para calcular una referencia de costos."
  } else {
    titleWhenEmpty =
      selectedMaterials.length > 0
        ? "Para continuar seleccioná una categoría"
        : showManualSecondEmptyState
          ? "Empezá a armar tu lista de materiales"
          : "Publicá tu lista y recibí presupuestos de proveedores"
    subTitleWhenEmpty =
      selectedMaterials.length > 0
        ? "Seleccioná una categoría o buscá materiales específicos para continuar con tu cotización."
        : showManualSecondEmptyState
          ? "Usá los filtros superiores para buscar por nombre o categoría. Si querés, empezá con materiales frecuentes."
          : "Elegí una categoría para empezar."
  }

  const autofillImportSummary = useMemo(() => {
    if (!compactMaterialsCenter || materialsAutofillDraftId == null || selectedMaterials.length === 0) {
      return null
    }
    const categoryCount = new Set(selectedMaterials.map((m) => m.category)).size
    const fromCatalogCount = selectedMaterials.filter((m) => !isMaterialOutOfCatalog(m)).length
    const customOrUnmatchedCount = selectedMaterials.filter((m) => isMaterialOutOfCatalog(m)).length
    return {
      lineCount: selectedMaterials.length,
      categoryCount,
      fromCatalogCount,
      customOrUnmatchedCount,
    }
  }, [compactMaterialsCenter, materialsAutofillDraftId, selectedMaterials])

  const estimationCatalogGapCount = useMemo(
    () => (isEstimacionDeCostos ? selectedMaterials.filter(isMaterialOutOfCatalog).length : 0),
    [isEstimacionDeCostos, selectedMaterials]
  )

  const unitToShow = (unit: string): string => {
    switch (unit) {
      case "m2":
        return "Metro cuadrado"
      case "m3":
        return "Metro cúbico"
      case "kg":
        return "Kilogramo"
      case "ml":
        return "Mililitro"
      case "cm":
        return "Centímetro"
      case "u":
        return "Unidad"
      case "l":
        return "Litro"
      case "cm2":
        return "Centímetro cuadrado"
      case "cm3":
        return "Centímetro cúbico"
      case "gr":
        return "Gramo"
      case "m":
        return "Metro"
      default:
        return unit
    }
  }

  const handlePreviewButtonClick = () => {
    setShowPreview(true)
  }

  const handleCreateCustomMaterialFromList = () => {
    setShowAddCustomPopup(true)
  }

  const breadcrumbsRightSlot =
    typeof document !== "undefined" ? document.getElementById("breadcrumbs-right-slot") : null

  const showMaterialsFileAnalysisInMainLayout = isMaterialsFileAnalyzing

  return (
    <>
      {isLoading ? (
        <CalculadoraDeMaterialesSkeleton />
      ) : (
        <div className={styles.container}>
          {!hideCreateTopHeader &&
            !showIntegratedMaterialsWelcome &&
            !showIntegratedEstimationWelcome &&
            !showManualSecondEmptyState && (
            <CalculadoraPageHeader
              headerTitle={headerTitle}
              headerSubtitle={headerSubtitle}
              isEstimacionDeCostos={isEstimacionDeCostos}
              userData={userData}
              selectedMaterials={selectedMaterials}
              setShowSendQuotePopup={setShowSendQuotePopup}
              handleSendQuoteClick={handleSendQuoteClick}
              hasChanges={hasChanges}
              isEditMode={isEditMode}
            />
          )}
          {(showIntegratedEstimationWelcome || showIntegratedMaterialsWelcome) &&
          !isMaterialsFileAnalyzing ? (
            <CalculadoraIntegratedWelcome
              showIntegratedEstimationWelcome={showIntegratedEstimationWelcome}
              totalCatalogMaterialsLabel={totalCatalogMaterialsLabel}
              onNavigateToCostEstimation={() => navigate("/cotizaciondemateriales/estimacion-costos")}
              onBeginManual={() => {
                if (showIntegratedEstimationWelcome) {
                  setEstimationWelcomeDismissed(true)
                } else {
                  setMaterialsWelcomeDismissed(true)
                }
                setSearchRequested(true)
                setTimeout(() => {
                  searchInputRef.current?.focus()
                  searchInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
                }, 100)
              }}
              onFileChosen={(f) => {
                void runMaterialsFile(f)
              }}
            />
          ) : (
          <>
            {(showManualSecondEmptyState ||
              showEstimationSecondEmptyState ||
              showReplaceAutofillFileAction) &&
              breadcrumbsRightSlot &&
              createPortal(
                <div className={styles.container_manualTopActions}>
                  {showReplaceAutofillFileAction ? (
                    <button
                      type="button"
                      className={styles.container_manualTopActions_replaceFileBtn}
                      onClick={handleRequestReplaceAutofillFile}
                    >
                      Analizar otro archivo
                    </button>
                  ) : null}
                  {showManualSecondEmptyState || showEstimationSecondEmptyState ? (
                    <button
                      type="button"
                      className={styles.container_manualTopActions_backBtn}
                      onClick={() => {
                        if (showEstimationSecondEmptyState) {
                          setEstimationWelcomeDismissed(false)
                        } else {
                          setMaterialsWelcomeDismissed(false)
                        }
                        setSearchRequested(false)
                        setSelectedCategory("")
                        setExpandedCategories([])
                        clearSearch()
                      }}
                    >
                      Volver al inicio
                    </button>
                  ) : null}
                </div>,
                breadcrumbsRightSlot
              )}
            <div className={styles.container_bodyContainer}>
              <div className={styles.container_bodyColumn}>
                {showMaterialsFileAnalysisInMainLayout ? (
                  <>
                    <div className={styles.container_body_headerSpacer} aria-hidden />
                    <div className={styles.container_body}>
                      <div className={styles.container_body_categoryMaterials}>
                        <div
                          className={styles.container_body_categoryMaterials_fileAnalysisLoader}
                          role="status"
                          aria-live="polite"
                          aria-busy
                        >
                          <MaterialsQuotationPipelineLoader
                            pipelinePhase={materialsFilePipeline}
                            stageProgress={materialsFileStageProgress}
                            primaryFileOriginalName={materialsFileLabel}
                            primaryFileSizeBytes={materialsFileSize}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {!(selectedCategory || selectedMaterials.length > 0 || searchRequested) && (
                      <div className={styles.container_body_headerSpacer} aria-hidden />
                    )}
                    {(selectedCategory || selectedMaterials.length > 0 || searchRequested) && (
                      <CalculadoraCatalogToolbar
                        categoryOptions={categoryOptions}
                        selectedCategory={selectedCategory}
                        toggleCategory={toggleCategory}
                        searchInputRef={searchInputRef}
                        searchInputActive={searchInputActive}
                        searchTerm={searchTerm}
                        handleSearchChange={handleSearchChange}
                        clearSearch={clearSearch}
                        filteredMaterials={filteredMaterials}
                        visibleMaterials={visibleMaterials}
                        searchHighlightIndex={searchHighlightIndex}
                        setSearchHighlightIndex={setSearchHighlightIndex}
                        toggleMaterial={selectMaterialFromSearch}
                        hasMoreResults={hasMoreResults}
                        searchResultsExpanded={searchResultsExpanded}
                        setSearchResultsExpanded={setSearchResultsExpanded}
                        setShowAddCustomPopup={setShowAddCustomPopup}
                        setSearchTerm={setSearchTerm}
                      />
                    )}
                    <div className={styles.container_body}>
                      <div className={styles.container_body_categoryMaterials}>
                        {expandedCategories && selectedCategory && expandedCategories.includes(selectedCategory) ? (
                          <CalculadoraExpandedMaterialsList
                            categories={categories}
                            selectedCategory={selectedCategory}
                            selectedMaterials={selectedMaterials}
                            isEstimacionDeCostos={isEstimacionDeCostos}
                            toggleMaterial={toggleMaterial}
                            onCreateCustomMaterialFromList={handleCreateCustomMaterialFromList}
                            onOpenMaterialDetail={openMaterialDetail}
                          />
                        ) : (
                          <CalculadoraMaterialsEmptyPanel
                            showManualSecondEmptyState={showManualSecondEmptyState}
                            compactMaterialsCenter={compactMaterialsCenter}
                            autofillImportSummary={autofillImportSummary}
                            analysisContext={materialsAutofillAnalysisContext}
                            titleWhenEmpty={titleWhenEmpty}
                            subTitleWhenEmpty={subTitleWhenEmpty}
                            categoryOptions={categoryOptions}
                            selectedCategory={selectedCategory}
                            toggleCategory={toggleCategory}
                            handleSearchLinkClick={handleSearchLinkClick}
                            setShowAddCustomPopup={setShowAddCustomPopup}
                            frequentMaterials={frequentMaterials}
                            onSelectFrequentMaterial={selectCategoryAndToggleMaterial}
                            isEstimacionDeCostos={isEstimacionDeCostos}
                            estimationCatalogGapCount={estimationCatalogGapCount}
                            onRequestQuotesFromEstimation={
                              estimationCatalogGapCount > 0 ? handleRequestQuotesFromEstimation : undefined
                            }
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            <CalculadoraSummaryColumn
              groupedMaterials={groupedMaterials}
              selectedMaterials={selectedMaterials}
              isEstimacionDeCostos={isEstimacionDeCostos}
              lastAddedMaterialId={lastAddedMaterialId}
              unitToShow={unitToShow}
              updateQuantity={updateQuantity}
              removeMaterial={removeMaterial}
              handlePreviewButtonClick={handlePreviewButtonClick}
              formattedTotal={formattedTotal}
              userData={userData}
              setShowSendQuotePopup={setShowSendQuotePopup}
              handleSendQuoteClick={handleSendQuoteClick}
              hasChanges={hasChanges}
              isEditMode={isEditMode}
              showTransferToEstimation={isCreateQuotationFlow && selectedMaterials.length > 0}
              onTransferToEstimation={handleTransferToEstimation}
              estimationCatalogGapCount={estimationCatalogGapCount}
              onRequestQuotesFromEstimation={
                estimationCatalogGapCount > 0 ? handleRequestQuotesFromEstimation : undefined
              }
            />
          </div>
          </>
          )}
          {showAddCustomPopup && (
            <AddCustomMaterialPopup
              onClose={() => setShowAddCustomPopup(false)}
              onAdd={addCustomMaterial}
              categories={categories}
              isEstimacionDeCostos={isEstimacionDeCostos}
            />
          )}
          {showSendQuotePopup && (
            <SendQuotePopup
              onClose={() => setShowSendQuotePopup(false)}
              materials={selectedMaterials}
              onSuccess={handleQuoteSuccess}
              setShowPreview={setShowPreview}
              autofillDraftId={materialsAutofillDraftId}
              categoryRowsForAutofill={
                categories.data?.map((c) => ({ id: c.id, name: c.name })) ?? []
              }
            />
          )}
          {showPreview && (
            <MaterialsListPreview
              materials={selectedMaterials}
              sendHandler={handleSendQuoteClick}
              setShowSendQuotePopup={setShowSendQuotePopup}
              isEstimacionDeCostos={isEstimacionDeCostos}
              closePreview={() => setShowPreview(false)}
            />
          )}
        </div>
      )}
      {showMaterialAddedToast && <CalculadoraMaterialAddedToast />}
    </>
  )
}

const mapStateToProps = (state: CalculadoraReduxState) => ({
  categoriesWithMaterials: state.category.categories,
  quotationData: state.profile.quotation,
  materialsBySubquotation: state.profile.materialsBySubquotation,
  userData: state.general.userData,
})

export default connect(mapStateToProps, {
  getCategoriesWithMaterials,
  getMaterialsBySubquotationId,
  editSubquotationMaterials,
  openMainModal,
  closeMainModal,
})(CalculadoraDeMateriales)
